<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LumeraInsightChart from './LumeraInsightChart.vue';
import ShopifyConversionFunnelInsight from './ShopifyConversionFunnelInsight.vue';
import ReportsAPI from 'dashboard/api/reports';

const props = defineProps({
  analysis: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['saved', 'drill']);
const { t, locale } = useI18n();

const copy = computed(() => ({
  generatedByLumera: t('LUMERA_INSIGHT.GENERATED_BY_LUMERA'),
  agentPerformance: t('LUMERA_INSIGHT.AGENT_PERFORMANCE'),
  agentsShown: t('LUMERA_INSIGHT.AGENTS_SHOWN'),
  agent: t('LUMERA_INSIGHT.AGENT'),
  conversations: t('LUMERA_INSIGHT.CONVERSATIONS'),
  resolved: t('LUMERA_INSIGHT.RESOLVED'),
  resolutionRate: t('LUMERA_INSIGHT.RESOLUTION_RATE'),
  firstResponse: t('LUMERA_INSIGHT.FIRST_RESPONSE'),
  resolutionTime: t('LUMERA_INSIGHT.RESOLUTION_TIME'),
  resourceAnalysis: t('LUMERA_INSIGHT.RESOURCE_ANALYSIS'),
  chartBar: t('LUMERA_INSIGHT.CHART_BAR'),
  chartLine: t('LUMERA_INSIGHT.CHART_LINE'),
  chartDoughnut: t('LUMERA_INSIGHT.CHART_DOUGHNUT'),
  pieByDay: t('LUMERA_INSIGHT.PIE_BY_DAY'),
  pieByWeek: t('LUMERA_INSIGHT.PIE_BY_WEEK'),
  pieByMonth: t('LUMERA_INSIGHT.PIE_BY_MONTH'),
  comparedWithPrevious: t('LUMERA_INSIGHT.COMPARED_WITH_PREVIOUS'),
  saveReport: t('LUMERA_INSIGHT.SAVE_REPORT'),
  save: t('LUMERA_INSIGHT.SAVE'),
  cancel: t('LUMERA_INSIGHT.CANCEL'),
  reportName: t('LUMERA_INSIGHT.REPORT_NAME'),
  schedule: t('LUMERA_INSIGHT.SCHEDULE'),
  noSchedule: t('LUMERA_INSIGHT.NO_SCHEDULE'),
  daily: t('LUMERA_INSIGHT.DAILY'),
  weekly: t('LUMERA_INSIGHT.WEEKLY'),
  monthly: t('LUMERA_INSIGHT.MONTHLY'),
  recipientEmail: t('LUMERA_INSIGHT.RECIPIENT_EMAIL'),
  saveError: t('LUMERA_INSIGHT.SAVE_ERROR'),
  source: t('LUMERA_INSIGHT.SOURCE'),
  drilldown: t('LUMERA_INSIGHT.DRILLDOWN'),
  export: t('LUMERA_INSIGHT.EXPORT_CSV'),
  exportExcel: t('LUMERA_INSIGHT.EXPORT_XLSX'),
  exportPdf: t('LUMERA_INSIGHT.EXPORT_PDF'),
  updatedAt: t('LUMERA_INSIGHT.UPDATED_AT'),
  insufficientData: t('LUMERA_INSIGHT.INSUFFICIENT_DATA'),
  labelSeparator: t('LUMERA_INSIGHT.LABEL_SEPARATOR'),
  reportSaved: t('LUMERA_INSIGHT.SAVED'),
  exportTimeout: t('LUMERA_INSIGHT.EXPORT_TIMEOUT'),
  exportUnavailable: t('LUMERA_INSIGHT.EXPORT_UNAVAILABLE'),
  exportProcessing: t('LUMERA_INSIGHT.EXPORT_PROCESSING'),
  exportWaiting: t('LUMERA_INSIGHT.EXPORT_WAITING'),
  exportPreparing: t('LUMERA_INSIGHT.EXPORT_PREPARING'),
  exportError: t('LUMERA_INSIGHT.EXPORT_ERROR'),
  csvQuestion: t('LUMERA_INSIGHT.CSV_QUESTION'),
  csvMetric: t('LUMERA_INSIGHT.CSV_METRIC'),
  csvValue: t('LUMERA_INSIGHT.CSV_VALUE'),
  csvSeries: t('LUMERA_INSIGHT.CSV_SERIES'),
}));

const dashboard = computed(() => props.analysis.dashboard || {});
const chart = computed(() => dashboard.value.chart || {});
const channelChart = computed(() => dashboard.value.channel_chart || {});
const mainChartType = ref('bar');
const isSaveFormOpen = ref(false);
const isSaving = ref(false);
const isExporting = ref(false);
const exportError = ref('');
const exportStatus = ref('');
const saveError = ref('');
const savedReportName = ref('');
const scheduleFrequency = ref('');
const scheduleEmail = ref('');
const chartData = computed(() => ({
  labels: chart.value.labels || [],
  datasets: (chart.value.datasets || []).map(dataset => ({
    label: dataset.label,
    data: dataset.data,
    backgroundColor: dataset.background_color,
    borderRadius: 8,
  })),
}));
const exportFilename = format => {
  const title = dashboard.value.title || 'analise-lumerai';
  const slug = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const now = new Date();
  const pad = value => String(value).padStart(2, '0');
  const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

  return `${slug || 'analise-lumerai'}-${timestamp}.${format}`;
};
const pieGranularity = computed(() => {
  const count = chartData.value.labels.length;
  if (count <= 14) return 'day';
  if (count <= 62) return 'week';

  return 'month';
});
const formatMonthLabel = month =>
  new Intl.DateTimeFormat(String(locale.value).replace('_', '-'), {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2024, Number(month) - 1, 1)));
const aggregatePieData = () => {
  const { labels, datasets } = chartData.value;
  if (pieGranularity.value === 'day') return chartData.value;

  const groups = [];
  labels.forEach((label, index) => {
    const key =
      pieGranularity.value === 'month'
        ? label.split('/')[1]
        : Math.floor(index / 7).toString();
    let group = groups.at(-1);
    if (!group || group.key !== key) {
      group = { key, start: label, end: label, indexes: [] };
      groups.push(group);
    }
    group.end = label;
    group.indexes.push(index);
  });

  return {
    labels: groups.map(group =>
      pieGranularity.value === 'month'
        ? formatMonthLabel(group.key)
        : `${group.start}–${group.end}`
    ),
    datasets: datasets.map(dataset => ({
      ...dataset,
      data: groups.map(group =>
        group.indexes.reduce(
          (total, index) => total + Number(dataset.data[index] || 0),
          0
        )
      ),
    })),
  };
};
const mainChartData = computed(() =>
  mainChartType.value === 'doughnut' ? aggregatePieData() : chartData.value
);
const mainChartTitle = computed(() => {
  if (mainChartType.value !== 'doughnut') return chart.value.title;

  return {
    day: copy.value.pieByDay,
    week: copy.value.pieByWeek,
    month: copy.value.pieByMonth,
  }[pieGranularity.value];
});
const channelChartData = computed(() => ({
  labels: channelChart.value.labels || [],
  datasets: (channelChart.value.datasets || []).map(dataset => ({
    label: dataset.label,
    data: dataset.data,
    backgroundColor: dataset.background_color,
    borderRadius: 8,
  })),
}));
const hasChannelData = computed(() => channelChart.value.labels?.length > 0);
const agents = computed(() => dashboard.value.agents || []);
const spotlight = computed(() => dashboard.value.agent_spotlight);
const lumerai = computed(() => dashboard.value.lumerai);
const resourceSections = computed(
  () => dashboard.value.resource_sections || []
);
const metadata = computed(() => dashboard.value.metadata || {});
const drilldowns = computed(() => dashboard.value.drilldowns || []);
const shopifyCommerceFunnel = computed(() =>
  props.analysis.analysis_context?.include_shopify_commerce_funnel
    ? props.analysis.shopify_commerce_funnel || null
    : null
);
const changeLabel = change =>
  `${change > 0 ? '+' : ''}${change}% ${copy.value.comparedWithPrevious}`;
const openSaveForm = () => {
  savedReportName.value = dashboard.value.title || '';
  scheduleFrequency.value = '';
  scheduleEmail.value = '';
  saveError.value = '';
  isSaveFormOpen.value = true;
};
const exportCsv = () => {
  const rows = [
    [copy.value.csvQuestion, props.analysis.query?.question || ''],
    ...(metadata.value.filters || []).map(filter => [
      filter.label,
      filter.value,
    ]),
    [],
    [copy.value.csvMetric, copy.value.csvValue],
    ...(dashboard.value.kpis || []).map(item => [item.label, item.value]),
    [],
    [chart.value.title || copy.value.csvSeries, copy.value.conversations],
    ...chartData.value.labels.map((label, index) => [
      label,
      chartData.value.datasets[0]?.data[index] || 0,
    ]),
    [],
    [
      copy.value.agent,
      copy.value.conversations,
      copy.value.resolved,
      copy.value.resolutionRate,
      copy.value.firstResponse,
    ],
    ...(agents.value || []).map(agent => [
      agent.name,
      agent.conversations_count,
      agent.resolved_conversations_count,
      agent.resolution_rate,
      agent.avg_first_response_time,
    ]),
    ...resourceSections.value.flatMap(section => [
      [],
      [section.title, ''],
      ...section.metrics.map(item => [item.label, item.value]),
    ]),
  ];
  const csv = `\uFEFF${rows
    .map(row =>
      row.map(value => `"${String(value).replaceAll('"', '""')}"`).join(';')
    )
    .join('\r\n')}`;
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = exportFilename('csv');
  link.click();
  URL.revokeObjectURL(url);
};
const pause = milliseconds =>
  new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
const waitForExport = (exportId, attempt = 0) => {
  if (attempt >= 60) return Promise.reject(new Error(copy.value.exportTimeout));

  return ReportsAPI.getLumeraExportStatus(exportId).then(({ data }) => {
    if (data.status === 'completed' && data.ready) return data;
    if (data.status === 'failed') {
      return Promise.reject(
        new Error(data.error_message || copy.value.exportUnavailable)
      );
    }

    exportStatus.value =
      data.status === 'processing'
        ? copy.value.exportProcessing
        : copy.value.exportWaiting;
    return pause(1000).then(() => waitForExport(exportId, attempt + 1));
  });
};
const exportNative = async format => {
  const filters = props.analysis.query?.filters || {};
  if (!props.analysis.query?.question || isExporting.value) return;

  isExporting.value = true;
  exportError.value = '';
  exportStatus.value = copy.value.exportPreparing;
  try {
    const { data: exportJob } = await ReportsAPI.exportLumera({
      format,
      analysis: {
        ...props.analysis,
        query: { ...props.analysis.query, filters },
      },
    });
    const completedExport = await waitForExport(exportJob.id);
    const { data } = await ReportsAPI.downloadLumeraExport(completedExport.id);
    const blob = data instanceof Blob ? data : new Blob([data]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportFilename(format);
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    link.remove();
    // Alguns navegadores iniciam o download no próximo ciclo de eventos.
    // Revogar a URL no mesmo instante pode cancelar PDF/XLSX maiores.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch (error) {
    // Mantém o detalhe técnico disponível para diagnóstico sem expor a
    // implementação ao usuário final.
    // eslint-disable-next-line no-console
    console.error('Falha ao baixar exportação LumerAI', error);
    exportError.value = copy.value.exportError;
  } finally {
    isExporting.value = false;
    exportStatus.value = '';
  }
};
const saveReport = async () => {
  if (!savedReportName.value.trim() || !props.analysis.query?.question) return;

  isSaving.value = true;
  saveError.value = '';
  try {
    const { data: savedReport } = await ReportsAPI.saveLumeraReport({
      name: savedReportName.value.trim(),
      question: props.analysis.query.question,
      filters: props.analysis.query.filters || {},
      layout: { chart_type: mainChartType.value },
    });
    if (scheduleFrequency.value && scheduleEmail.value.trim()) {
      await ReportsAPI.scheduleLumeraReport({
        reportId: savedReport.id,
        recipientEmail: scheduleEmail.value.trim(),
        frequency: scheduleFrequency.value,
      });
    }
    isSaveFormOpen.value = false;
    emit('saved');
  } catch {
    saveError.value = copy.value.saveError;
  } finally {
    isSaving.value = false;
  }
};
const periodLabel = computed(() => {
  const period = props.analysis.period;
  if (!period) return '';

  const format = timestamp =>
    new Date(timestamp * 1000).toLocaleDateString(
      String(locale.value).replace('_', '-')
    );

  return `${format(period.since)} — ${format(period.until)}`;
});
const updatedAtLabel = computed(() => {
  if (!props.analysis.generated_at) return '';

  return new Date(props.analysis.generated_at * 1000).toLocaleString(
    String(locale.value).replace('_', '-')
  );
});
</script>

<template>
  <section class="rounded-2xl border border-n-strong bg-n-alpha-2 p-5 sm:p-6">
    <div
      class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
    >
      <div>
        <p
          class="mb-1 text-xs font-medium uppercase tracking-wide text-blue-500"
        >
          {{ copy.generatedByLumera }}
        </p>
        <h2 class="text-heading-2 text-n-slate-12">{{ dashboard.title }}</h2>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          {{ dashboard.description }}
        </p>
      </div>
      <div class="text-right text-xs text-n-slate-10">
        <p class="mb-0">{{ periodLabel }}</p>
        <p v-if="updatedAtLabel" class="mb-0 mt-1">
          {{ copy.updatedAt }} {{ updatedAtLabel }}
        </p>
      </div>
    </div>

    <div class="mt-4 flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="rounded-md bg-n-surface-1 px-3 py-2 text-xs font-medium text-n-slate-12 transition hover:bg-n-alpha-2"
        @click="openSaveForm"
      >
        {{ copy.saveReport }}
      </button>
      <form
        v-if="isSaveFormOpen"
        class="flex flex-wrap items-center gap-2"
        @submit.prevent="saveReport"
      >
        <input
          v-model="savedReportName"
          :placeholder="copy.reportName"
          class="rounded-md border border-n-strong bg-n-surface-1 px-3 py-2 text-xs text-n-slate-12"
        />
        <select
          v-model="scheduleFrequency"
          class="rounded-md border border-n-strong bg-n-surface-1 px-3 py-2 text-xs text-n-slate-12"
        >
          <option value="">{{ copy.noSchedule }}</option>
          <option value="daily">{{ copy.daily }}</option>
          <option value="weekly">{{ copy.weekly }}</option>
          <option value="monthly">{{ copy.monthly }}</option>
        </select>
        <input
          v-if="scheduleFrequency"
          v-model="scheduleEmail"
          type="email"
          :placeholder="copy.recipientEmail"
          class="rounded-md border border-n-strong bg-n-surface-1 px-3 py-2 text-xs text-n-slate-12"
          required
        />
        <button
          type="submit"
          class="rounded-md bg-blue-500 px-3 py-2 text-xs font-medium text-white"
          :disabled="isSaving"
        >
          {{ copy.save }}
        </button>
        <button
          type="button"
          class="rounded-md px-3 py-2 text-xs text-n-slate-11"
          @click="isSaveFormOpen = false"
        >
          {{ copy.cancel }}
        </button>
      </form>
      <span v-if="saveError" class="text-xs text-red-500">{{ saveError }}</span>
      <button
        type="button"
        class="rounded-md px-3 py-2 text-xs font-medium text-blue-500 transition hover:bg-blue-500/10"
        :disabled="isExporting"
        @click="exportCsv"
      >
        {{ copy.export }}
      </button>
      <button
        type="button"
        class="rounded-md px-3 py-2 text-xs font-medium text-blue-500 transition hover:bg-blue-500/10"
        :disabled="isExporting"
        @click="exportNative('xlsx')"
      >
        {{ copy.exportExcel }}
      </button>
      <button
        type="button"
        class="rounded-md px-3 py-2 text-xs font-medium text-blue-500 transition hover:bg-blue-500/10"
        :disabled="isExporting"
        @click="exportNative('pdf')"
      >
        {{ copy.exportPdf }}
      </button>
    </div>
    <p v-if="exportError" class="mb-0 mt-2 text-xs text-red-500">
      {{ exportError }}
    </p>
    <p v-else-if="exportStatus" class="mb-0 mt-2 text-xs text-n-slate-10">
      {{ exportStatus }}
    </p>

    <div v-if="metadata.filters?.length" class="mt-4 flex flex-wrap gap-2">
      <span
        v-for="filter in metadata.filters"
        :key="filter.label"
        class="rounded-full bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-11"
      >
        {{ filter.label }}{{ copy.labelSeparator }}
        <strong class="font-medium text-n-slate-12">{{ filter.value }}</strong>
      </span>
    </div>
    <div
      v-if="metadata.source || metadata.definition"
      class="mt-4 rounded-lg border border-n-strong bg-n-surface-1 p-3 text-xs leading-5 text-n-slate-11"
    >
      <p class="mb-1 font-medium text-n-slate-12">{{ copy.source }}</p>
      <p v-if="metadata.source" class="mb-1">{{ metadata.source }}</p>
      <p class="mb-0">{{ metadata.definition }}</p>
    </div>
    <div
      v-if="dashboard.empty_state"
      class="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-500"
    >
      <p class="mb-0">
        <strong>{{ copy.insufficientData }}{{ copy.labelSeparator }}</strong>
        {{ dashboard.empty_state }}
      </p>
    </div>

    <div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <article
        v-for="item in dashboard.kpis || []"
        :key="item.label"
        class="rounded-xl bg-n-surface-1 p-4"
      >
        <p class="mb-1 text-sm text-n-slate-11">{{ item.label }}</p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ item.value }}
        </p>
        <p
          v-if="item.change !== null && item.change !== undefined"
          class="mb-0 mt-1 text-xs font-medium"
          :class="item.change >= 0 ? 'text-green-500' : 'text-red-500'"
        >
          {{ changeLabel(item.change) }}
        </p>
      </article>
    </div>

    <ShopifyConversionFunnelInsight :funnel="shopifyCommerceFunnel" />

    <div class="mt-5 rounded-xl bg-n-surface-1 p-4 sm:p-5">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ mainChartTitle }}
      </h3>
      <div class="mt-3 flex flex-wrap gap-2">
        <button
          v-for="option in [
            { value: 'bar', label: copy.chartBar },
            { value: 'line', label: copy.chartLine },
            { value: 'doughnut', label: copy.chartDoughnut },
          ]"
          :key="option.value"
          type="button"
          class="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
          :class="
            mainChartType === option.value
              ? 'bg-blue-500 text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12'
          "
          @click="mainChartType = option.value"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="mt-4 h-72">
        <LumeraInsightChart :collection="mainChartData" :type="mainChartType" />
      </div>
    </div>

    <div
      v-if="dashboard.channel_chart || spotlight"
      class="mt-5 grid gap-5 xl:grid-cols-2"
    >
      <div
        v-if="dashboard.channel_chart"
        class="rounded-xl bg-n-surface-1 p-4 sm:p-5"
      >
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ channelChart.title }}
        </h3>
        <div v-if="hasChannelData" class="mt-4 h-64">
          <LumeraInsightChart :collection="channelChartData" type="doughnut" />
        </div>
        <p v-else class="mb-0 mt-4 text-sm text-n-slate-10">
          {{ channelChart.empty_message }}
        </p>
      </div>

      <div v-if="spotlight" class="rounded-xl bg-n-surface-1 p-4 sm:p-5">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ spotlight.title }}
        </h3>
        <div class="mt-4 grid grid-cols-2 gap-3">
          <article
            v-for="item in spotlight.metrics || []"
            :key="item.label"
            class="rounded-lg bg-n-alpha-2 p-3"
          >
            <p class="mb-1 text-xs text-n-slate-11">{{ item.label }}</p>
            <p class="mb-0 text-lg font-semibold text-n-slate-12">
              {{ item.value }}
            </p>
          </article>
        </div>
      </div>
    </div>

    <div v-if="agents.length" class="mt-5 rounded-xl bg-n-surface-1 p-4 sm:p-5">
      <div class="flex items-baseline justify-between gap-3">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ copy.agentPerformance }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{ agents.length }} {{ copy.agentsShown }}
        </span>
      </div>
      <div class="mt-4 overflow-x-auto">
        <table class="w-full min-w-[680px] text-left text-sm">
          <thead class="border-b border-n-strong text-xs text-n-slate-10">
            <tr>
              <th class="px-2 py-3 font-medium">{{ copy.agent }}</th>
              <th class="px-2 py-3 text-right font-medium">
                {{ copy.conversations }}
              </th>
              <th class="px-2 py-3 text-right font-medium">
                {{ copy.resolved }}
              </th>
              <th class="px-2 py-3 text-right font-medium">
                {{ copy.resolutionRate }}
              </th>
              <th class="px-2 py-3 text-right font-medium">
                {{ copy.firstResponse }}
              </th>
              <th class="px-2 py-3 text-right font-medium">
                {{ copy.resolutionTime }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-strong text-n-slate-12">
            <tr v-for="agent in agents" :key="agent.id">
              <td class="px-2 py-3 font-medium">{{ agent.name }}</td>
              <td class="px-2 py-3 text-right">
                {{ agent.conversations_count }}
              </td>
              <td class="px-2 py-3 text-right">
                {{ agent.resolved_conversations_count }}
              </td>
              <td class="px-2 py-3 text-right">{{ agent.resolution_rate }}</td>
              <td class="px-2 py-3 text-right">
                {{ agent.avg_first_response_time }}
              </td>
              <td class="px-2 py-3 text-right">
                {{ agent.avg_resolution_time }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="lumerai" class="mt-5 rounded-xl bg-n-surface-1 p-4 sm:p-5">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ lumerai.title }}
      </h3>
      <div class="mt-4 grid gap-3 sm:grid-cols-2">
        <article
          v-for="item in lumerai.metrics || []"
          :key="item.label"
          class="rounded-lg bg-n-alpha-2 p-3"
        >
          <p class="mb-1 text-sm text-n-slate-11">{{ item.label }}</p>
          <p class="mb-0 text-xl font-semibold text-n-slate-12">
            {{ item.value }}
          </p>
        </article>
      </div>
    </div>

    <div v-if="resourceSections.length" class="mt-5">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ copy.resourceAnalysis }}
      </h3>
      <div class="mt-4 grid gap-4 lg:grid-cols-2">
        <article
          v-for="section in resourceSections"
          :key="section.title"
          class="rounded-xl bg-n-surface-1 p-4"
        >
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ section.title }}
          </h4>
          <div class="mt-4 grid grid-cols-2 gap-3">
            <div
              v-for="item in section.metrics || []"
              :key="item.label"
              class="rounded-lg bg-n-alpha-2 p-3"
            >
              <p class="mb-1 text-xs text-n-slate-11">{{ item.label }}</p>
              <p class="mb-0 text-lg font-semibold text-n-slate-12">
                {{ item.value }}
              </p>
              <p
                v-if="item.change !== null && item.change !== undefined"
                class="mb-0 mt-1 text-xs font-medium"
                :class="item.change >= 0 ? 'text-green-500' : 'text-red-500'"
              >
                {{ changeLabel(item.change) }}
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>

    <article
      class="mt-5 whitespace-pre-line rounded-xl bg-n-surface-1 p-4 text-sm leading-6 text-n-slate-12"
    >
      {{ analysis.answer }}
    </article>

    <div v-if="drilldowns.length" class="mt-5">
      <p class="mb-2 text-sm font-medium text-n-slate-12">
        {{ copy.drilldown }}
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="drilldown in drilldowns"
          :key="drilldown"
          type="button"
          class="rounded-md border border-n-strong px-3 py-2 text-xs text-n-slate-11 transition hover:border-blue-500 hover:text-blue-500"
          @click="emit('drill', drilldown)"
        >
          {{ drilldown }}
        </button>
      </div>
    </div>
  </section>
</template>
